// frontend/src/pages/NotificationPrefsPage.jsx
/**
 * NotificationPrefsPage — personal notification preferences (P12-G12).
 *
 * Route: /settings/notifications (inside SettingsLayout)
 *
 * Two sections:
 *   1. Email Notifications — email toggles + digest frequency radio
 *   2. In-App Notifications — bell notification toggles
 *
 * Saves are debounced: quick consecutive toggle changes are batched into a
 * single PUT /api/notifications/preferences after 500 ms of inactivity.
 * Local state is updated optimistically so the UI feels instant.
 */

import React, { useEffect, useRef, useState } from "react";
import toast from "react-hot-toast";
import { getNotificationPrefs, updateNotificationPrefs } from "../api/notifications";
import Spinner from "../components/Spinner";

// ─── Config ───────────────────────────────────────────────────────────────── //

const EMAIL_TOGGLES = [
  {
    key: "email_scan_milestone",
    label: "Scan milestones",
    description:
      "Receive an email when a QR code reaches 100 / 1 k / 10 k / 100 k scans.",
  },
  {
    key: "email_payment_events",
    label: "Payment events",
    description: "Receive an email on payment failures and plan changes.",
  },
  {
    key: "email_member_activity",
    label: "Member activity",
    description:
      "Receive an email when someone joins or leaves your organisation.",
  },
  {
    key: "email_product_updates",
    label: "Product updates",
    description:
      "Announcements, new features, and tips from the QRFlow team.",
  },
];

const INAPP_TOGGLES = [
  {
    key: "inapp_scan_milestone",
    label: "Scan milestones",
    description:
      "Show a bell notification when a QR code hits a scan milestone.",
  },
  {
    key: "inapp_member_activity",
    label: "Member activity",
    description:
      "Show a bell notification when an org member joins or leaves.",
  },
];

const DIGEST_OPTIONS = [
  {
    value: "immediate",
    label: "Immediate",
    description:
      "Each notification triggers an individual email as soon as it happens.",
  },
  {
    value: "daily",
    label: "Daily digest",
    description:
      "All notifications are batched into one email sent at 08:00 UTC each day.",
  },
  {
    value: "weekly",
    label: "Weekly digest",
    description:
      "All notifications are batched into one email sent at 08:00 UTC every Monday.",
  },
];

/** Debounce delay in milliseconds between the last toggle and the API call. */
const DEBOUNCE_MS = 500;

// ─── Sub-components ────────────────────────────────────────────────────────── //

/**
 * Toggle switch — an accessible role="switch" styled button.
 *
 * @param {{ checked: boolean, disabled: boolean, onChange: () => void, label: string, description: string }} props
 */
function Toggle({ checked, disabled, onChange, label, description }) {
  return (
    <div className="flex items-center justify-between py-3">
      <div className="pr-4">
        <p className="text-sm font-medium text-gray-800">{label}</p>
        <p className="text-xs text-gray-500 mt-0.5">{description}</p>
      </div>
      <button
        type="button"
        role="switch"
        aria-checked={checked}
        onClick={onChange}
        disabled={disabled}
        className={`relative inline-flex h-5 w-9 shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 focus-visible:ring-offset-1 disabled:opacity-50 disabled:cursor-not-allowed ${
          checked ? "bg-blue-600" : "bg-gray-200"
        }`}
      >
        <span className="sr-only">Toggle {label}</span>
        <span
          aria-hidden="true"
          className={`pointer-events-none inline-block h-4 w-4 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out ${
            checked ? "translate-x-4" : "translate-x-0"
          }`}
        />
      </button>
    </div>
  );
}

// ─── Page ─────────────────────────────────────────────────────────────────── //

export default function NotificationPrefsPage() {
  const [prefs, setPrefs] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  // Accumulate pending changes between debounce flushes.
  const pendingRef = useRef({});
  const timerRef = useRef(null);

  // ── Load ────────────────────────────────────────────────────────────────── //
  useEffect(() => {
    getNotificationPrefs()
      .then(setPrefs)
      .catch(() => toast.error("Could not load notification preferences."))
      .finally(() => setLoading(false));

    return () => {
      // Flush any pending save on unmount to avoid a fire-and-forget
      if (timerRef.current) clearTimeout(timerRef.current);
    };
  }, []);

  // ── Debounced save ──────────────────────────────────────────────────────── //
  /**
   * Apply *delta* optimistically to local state, then schedule a debounced
   * PUT.  Rapid consecutive calls accumulate into one request.
   */
  const scheduleSave = (delta) => {
    // Merge into pending batch.
    pendingRef.current = { ...pendingRef.current, ...delta };

    if (timerRef.current) clearTimeout(timerRef.current);
    timerRef.current = setTimeout(async () => {
      const updates = { ...pendingRef.current };
      pendingRef.current = {};
      setSaving(true);
      try {
        const updated = await updateNotificationPrefs(updates);
        setPrefs(updated);
      } catch {
        toast.error("Could not save preferences. Please try again.");
        // Re-fetch to restore server state on failure.
        getNotificationPrefs().then(setPrefs).catch(() => null);
      } finally {
        setSaving(false);
      }
    }, DEBOUNCE_MS);
  };

  const handleToggle = (key) => {
    if (!prefs) return;
    const newValue = !prefs[key];
    // Optimistic update — UI feels instant.
    setPrefs((prev) => ({ ...prev, [key]: newValue }));
    scheduleSave({ [key]: newValue });
  };

  const handleFrequency = (value) => {
    if (!prefs || prefs.email_digest_frequency === value) return;
    setPrefs((prev) => ({ ...prev, email_digest_frequency: value }));
    scheduleSave({ email_digest_frequency: value });
  };

  // ── Render ──────────────────────────────────────────────────────────────── //
  if (loading) {
    return (
      <div className="flex justify-center py-16">
        <Spinner />
      </div>
    );
  }

  return (
    <div className="max-w-2xl space-y-6">
      <div>
        <h1 className="text-xl font-bold text-gray-900 mb-1">
          Notification preferences
        </h1>
        <p className="text-sm text-gray-500">
          Control which notifications you receive and how they are delivered.
          {saving && (
            <span className="ml-2 text-blue-500 animate-pulse">Saving…</span>
          )}
        </p>
      </div>

      {/* ── Section 1: Email Notifications ──────────────────────────────── */}
      <section className="bg-white rounded-2xl shadow-sm border border-gray-200 p-6 space-y-5">
        <div>
          <h2 className="text-base font-semibold text-gray-800">
            Email Notifications
          </h2>
          <p className="text-sm text-gray-500 mt-0.5">
            Choose which events trigger email notifications and how they are
            delivered.
          </p>
        </div>

        {/* Email toggles */}
        <div className="divide-y divide-gray-100">
          {EMAIL_TOGGLES.map(({ key, label, description }) => (
            <Toggle
              key={key}
              label={label}
              description={description}
              checked={!!prefs?.[key]}
              disabled={saving}
              onChange={() => handleToggle(key)}
            />
          ))}
        </div>

        {/* Delivery frequency */}
        <div className="pt-2 space-y-3">
          <div>
            <p className="text-sm font-medium text-gray-700">
              Email delivery frequency
            </p>
            <p className="text-xs text-gray-500 mt-0.5">
              How individual email notifications are batched before sending.
            </p>
          </div>
          {DIGEST_OPTIONS.map(({ value, label, description }) => (
            <label
              key={value}
              className={`flex items-start gap-3 p-3 rounded-xl border cursor-pointer transition-colors ${
                prefs?.email_digest_frequency === value
                  ? "border-blue-500 bg-blue-50"
                  : "border-gray-200 hover:bg-gray-50"
              }`}
            >
              <input
                type="radio"
                name="email_digest_frequency"
                value={value}
                checked={prefs?.email_digest_frequency === value}
                onChange={() => handleFrequency(value)}
                disabled={saving}
                className="mt-0.5 accent-blue-600"
              />
              <div>
                <p className="text-sm font-medium text-gray-800">{label}</p>
                <p className="text-xs text-gray-500 mt-0.5">{description}</p>
              </div>
            </label>
          ))}
        </div>
      </section>

      {/* ── Section 2: In-App Notifications ─────────────────────────────── */}
      <section className="bg-white rounded-2xl shadow-sm border border-gray-200 p-6 space-y-4">
        <div>
          <h2 className="text-base font-semibold text-gray-800">
            In-App Notifications
          </h2>
          <p className="text-sm text-gray-500 mt-0.5">
            Control which events appear in the notification bell in the top
            navigation bar.
          </p>
        </div>
        <div className="divide-y divide-gray-100">
          {INAPP_TOGGLES.map(({ key, label, description }) => (
            <Toggle
              key={key}
              label={label}
              description={description}
              checked={!!prefs?.[key]}
              disabled={saving}
              onChange={() => handleToggle(key)}
            />
          ))}
        </div>
      </section>
    </div>
  );
}
